import { UserProfile, Task, ApiError, TaskStatusResponse, SearchResponse } from '../types';

// const API_BASE_URL = 'http://localhost:3002';
const API_BASE_URL = 'https://latentbridge-api-93321750063.us-east4.run.app';

// Helper function for API requests
const apiRequest = async <T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`;
  const fetchOptions: RequestInit = {
    ...options,
    credentials: 'include', // Include cookies with every request
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, fetchOptions);
    
    if (!response.ok) {
      const errorData: { error: ApiError } = await response.json();
      throw errorData.error;
    }
    
    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Get current user profile
export const getCurrentUser = async (): Promise<UserProfile> => {
  return apiRequest<UserProfile>('/api/me');
};

// Get Google access token for Drive API
export const getGoogleToken = async (): Promise<{ accessToken: string; expiryDate: number }> => {
  return apiRequest<{ accessToken: string; expiryDate: number }>('/api/google/token');
};

// Create a new task with selected file IDs
export const createTask = async (fileIds: string[]): Promise<{ task_id: string }> => {
  return apiRequest<{ task_id: string }>('/api/tasks', {
    method: 'POST',
    body: JSON.stringify({ fileIds }),
  });
};

// Get task status by ID
export const getTaskById = async (taskId: string): Promise<{ tasks: Task[] }> => {
  const response = await apiRequest<{ task?: Task, tasks?: Task[] }>(`/api/tasks/${taskId}`);
  
  // Handle both response formats: 'task' for single object or 'tasks' for array
  if (response.task) {
    return { tasks: [response.task] };
  }
  
  return { tasks: response.tasks || [] };
};

// Get task status from the new API endpoint
export const getTaskStatus = async (taskId: string): Promise<TaskStatusResponse> => {
  return apiRequest<TaskStatusResponse>(`/api/tasks/${taskId}/status`);
};

// Search for content with optional parameters
export const searchContent = async (
  query: string,
  limit?: number,
  genesisTaskId?: string
): Promise<SearchResponse> => {
  // Build the query string
  const params = new URLSearchParams();
  params.append('query', query);
  
  if (limit) {
    params.append('limit', limit.toString());
  }
  
  if (genesisTaskId) {
    params.append('genesisTaskId', genesisTaskId);
  }
  
  return apiRequest<SearchResponse>(`/api/search?${params.toString()}`);
};

// Start Google OAuth flow
export const startGoogleAuth = (): void => {
  window.location.href = `${API_BASE_URL}/auth/google`;
};

// Logout the user
export const logout = async (): Promise<void> => {
  await fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'GET',
    credentials: 'include',
  });
}; 